var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ECollectionBannerState, EOwnedCollectionStatus, } from '../types';
import { useOwnedCollections } from '../ownedCollections';
import useBannersAvailabilityState from './useBannersAvailabilityState';
import { loggedSelector } from '../../../selectors';
var OUTDATED_COLLECTION_TYPE = 'outdatedButStartedByUser';
/*
  Order of the Collections in the inventory, based on the different states:
  1. Campaign visible (after ShowFrom) and active (after availableFrom and before availableUntil) and User started it (uploaded a card)
  2. Campaign visible (after showFrom) and active (after availableFrom and before availableUntil) and user did not start it (did not uploaded card yet)
  3. campaign visible (after showFrom) and before availableFrom date
  4. campaign visible (after showFrom) and active (after availableFrom and before availableUntil) and user submitted it
  5. campaign not active (after availableUntil) that user submitted
  6. campaign not active (after availableUntil) that user not submitted but started (uploaded at least one card)
  7. campaign not active (after availableUntil) that user not submitted and not even started (did not upload any single card)
  8. campaign after show Until that user submitted
  9. campaign after showUntil that user not submitted but started (uploaded at least one card)
Function below returns a number according to description above
*/
function getValueForSort(collectionData) {
    // it is after showUntil, so it is only visible because user started or completed it
    if (collectionData.state === OUTDATED_COLLECTION_TYPE) {
        return collectionData.progressStatus === EOwnedCollectionStatus.COMPLETED ? 8 : 9;
    }
    if (collectionData.state === ECollectionBannerState.EXPIRED) {
        if (collectionData.progressStatus)
            return collectionData.progressStatus === EOwnedCollectionStatus.COMPLETED ? 5 : 6;
        return 7;
    }
    if (collectionData.state === ECollectionBannerState.AVAILABLE) {
        if (collectionData.progressStatus)
            return collectionData.progressStatus === EOwnedCollectionStatus.COMPLETED ? 4 : 1;
        return 2;
    }
    if (collectionData.state === ECollectionBannerState.UPCOMING) {
        return 3;
    }
    return 10;
}
var useCollectionBannersList = function () {
    var _a = useOwnedCollections(), usersOwnedCollections = _a.items, lastFailedFetch = _a.lastFailedFetch, lastSuccessfulFetch = _a.lastSuccessfulFetch;
    var isLoggedIn = useSelector(loggedSelector);
    var isLoadingOwnedCollections = useMemo(function () {
        return (!lastFailedFetch && !lastSuccessfulFetch);
    }, [lastFailedFetch, lastSuccessfulFetch]);
    var bannersWithAvailabilityState = useBannersAvailabilityState();
    return useMemo(function () {
        if (isLoggedIn && isLoadingOwnedCollections) {
            return [];
        }
        // banners fetch for inventory
        var basicBannersObject = bannersWithAvailabilityState
            .reduce(function (basicBanners, collectionBanner) {
            var _a;
            return (__assign(__assign({}, basicBanners), (_a = {}, _a[collectionBanner._id] = collectionBanner, _a)));
        }, {});
        // adding ownedBanner or overwriting with owned one or removing if collection already hidden
        var allBannersObject = usersOwnedCollections.reduce(function (collectionsObject, usersOwnedCollection) {
            var _a;
            var ownedCollectionId = usersOwnedCollection.collectionCampaign._id;
            var associatedCollection = collectionsObject[ownedCollectionId];
            var isOwnedCollectionHidden = usersOwnedCollection.collectionCampaign.hideInInventory === true || usersOwnedCollection.hidden;
            // remove banners that was hidden by user
            if (isOwnedCollectionHidden) {
                if (associatedCollection)
                    delete collectionsObject[ownedCollectionId];
                return collectionsObject;
            }
            var collectionState = associatedCollection === null || associatedCollection === void 0 ? void 0 : associatedCollection.state;
            // if no collectionState the collection was not found in fetched basic list - we mark it as outdated
            // (it was started or completed by user but now it's past its showUntil)
            return __assign(__assign({}, collectionsObject), (_a = {}, _a[ownedCollectionId] = __assign(__assign({}, usersOwnedCollection.collectionCampaign), { state: collectionState || OUTDATED_COLLECTION_TYPE, progressStatus: usersOwnedCollection.status }), _a));
        }, basicBannersObject);
        var collectionsBannersSorted = Object.values(allBannersObject)
            .sort(function (obj1, obj2) { return getValueForSort(obj1) - getValueForSort(obj2); });
        return collectionsBannersSorted
            .map(function (collectionBanner) {
            var state = collectionBanner.state, progressStatus = collectionBanner.progressStatus, rest = __rest(collectionBanner, ["state", "progressStatus"]);
            // for outdated collection we set status for 'completed' or 'expired'
            if (state === OUTDATED_COLLECTION_TYPE) {
                return __assign(__assign({}, rest), { state: progressStatus === EOwnedCollectionStatus.COMPLETED
                        ? ECollectionBannerState.COMPLETED
                        : ECollectionBannerState.EXPIRED });
            }
            // for shown collection state should be changed only if it was completed
            var shownCollectionStatus = progressStatus === EOwnedCollectionStatus.COMPLETED
                ? ECollectionBannerState.COMPLETED
                : state;
            return __assign(__assign({}, rest), { state: shownCollectionStatus });
        });
    }, [
        isLoadingOwnedCollections,
        isLoggedIn,
        bannersWithAvailabilityState,
        usersOwnedCollections,
    ]);
};
export default useCollectionBannersList;
